import { ArrowRight } from "lucide-react";
import React from "react";
import { useClickOutside, useLocationQuery } from "../../hooks";
import { Button } from "../Button";
import Flex from "../Flex/Flex";
import { Typography } from "../Typography";
import { FiltersProvider } from "./FiltersContext";
import FiltersRenderer from "./FiltersRenderer";
import * as S from "./styled";
import { IFiltersContext, IFiltersProps } from "./types";

const Filters = <TQuery extends string = string>({
    onClose,
    onClear,
    title = "Filters",
    disableClearFiltersButton,
    clearFiltersTitle = "Clear all",
    filters,
    trackClearFiltersEventName = "clearall",
    trackCloseEventName = "close",
    disableAnimation = false
}: IFiltersProps<TQuery>): JSX.Element => {
    const query = useLocationQuery<TQuery>();
    const ref = useClickOutside(() => onClose());

    const handleReset: React.MouseEventHandler<HTMLButtonElement> = () => {
        query.clear();
        onClear?.();
    };

    const renderFilters: IFiltersContext<TQuery>["renderFilters"] = filtersToRender => (
        <FiltersRenderer<TQuery> filters={filtersToRender} />
    );

    const context = React.useMemo<IFiltersContext<TQuery>>(
        () => ({
            query,
            renderFilters
        }),
        [query]
    );

    return (
        <S.PopupOverlay open $disableAnimation={disableAnimation}>
                <FiltersProvider value={context}>
                    <S.FilterWrapper ref={ref} $disableAnimation={disableAnimation}>
                        <Flex spacing={2} width="100%" alignItems="center" justifyContent="space-between">
                            <Flex spacing={2} alignItems="center">
                                <Typography as="h2" variant="label-large-medium" color="text-primary-1200">
                                    {title}
                                </Typography>
                                <Button
                                    disabled={disableClearFiltersButton}
                                    onClick={handleReset}
                                    title={clearFiltersTitle}
                                    trackName={trackClearFiltersEventName}
                                    variant="secondary"
                                />
                            </Flex>
                            <Button
                                icon={<ArrowRight />}
                                onClick={() => onClose()}
                                trackName={trackCloseEventName}
                                variant="tertiary"
                            />
                        </Flex>
                        <Flex width="100%">{renderFilters(filters)}</Flex>
                    </S.FilterWrapper>
                </FiltersProvider>
        </S.PopupOverlay>
    );
};

export default Filters;
